import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useLocation } from 'react-router-dom';
import { Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from ?? '/workspaces';

  // Auto-start the redirect if the user lands here clean and isn't already
  // mid-flow. This shaves a click for the common "deep link → 401 → login"
  // path. Translators specifically asked for this in the cutover spec.
  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator) {
      void auth.signinRedirect({ state: from });
    }
  }, [auth, from]);

  if (auth.isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className="flex h-full items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-6 rounded-lg border bg-card p-8 text-card-foreground shadow-sm">
        <div className="flex items-center gap-2">
          <Languages className="size-6 text-primary" aria-hidden />
          <h1 className="text-xl font-semibold tracking-tight">Fluentix</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Sign in with your CoreFi account to manage translations.
        </p>
        {auth.error ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
            {auth.error.message}
          </p>
        ) : null}
        <Button
          className="w-full"
          onClick={() => void auth.signinRedirect({ state: from })}
          disabled={auth.isLoading}
        >
          {auth.isLoading ? 'Redirecting…' : 'Continue with CoreFi Account'}
        </Button>
      </div>
    </div>
  );
}
